import React from "react";
import { Col, Row, Skeleton, Tag, Tooltip, Typography } from "antd";
import moment from "moment";
import { InfoCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { api } from "../../client";
import {
  ApiPaths,
  Account,
} from "@/providers/smarta-banking/banking-api-client";
import { DISPLAY_DATE_TIME_FORMAT } from "@/v1/constants";
import { Label, SubValue, Td, Tr, Value } from "../../../components/styles";
import { formatAmountCurrency } from "@/v1/utils";
import Copyable from "@/v1/components/common/Copyable";
import { foundationColors } from "@/v1/themes/colors";

const getTexts = (t: TFunction) => ({
  labels: {
    untitledAccount: t("accountRow.labels.untitledAccount"),
    details: t("accountRow.labels.details"),
    accountHolderName: t("accountRow.labels.accountHolderName"),
    balance: t("accountRow.labels.balance"),
    iban: t("accountRow.labels.iban"),
    accountNumber: t("accountRow.labels.accountNumber"),
    lastSyncedAt: t("accountRow.labels.lastSyncedAt"),
    lastTransactionsAddedAt: t("accountRow.labels.lastTransactionsAddedAt"),
    archivedAt: t("accountRow.labels.archivedAt"),
    archived: t("accountRow.labels.archived"),
    active: t("accountRow.labels.active"),
  },
});

type Props = {
  customerId: string;
  account: Account;
};

export const AccountRow: React.FC<Props> = ({ customerId, account }) => {
  const { t } = useTranslation("Banking");
  const texts = getTexts(t);
  const { data: accountDetails, isLoading } = api.useQuery(
    "get",
    ApiPaths.getApiCustomersByCustomerIdAccountsByAccountId,
    {
      params: { path: { customerId, accountId: account.id } },
    }
  );

  return (
    <Tr>
      <Td>
        <Row align="middle" gutter={16} wrap={false}>
          <Col>
            <Value>{account.name || texts.labels.untitledAccount}</Value>
            {account.product && <SubValue>{account.product}</SubValue>}
          </Col>

          {(account.details || account.ownerName) && (
            <Col>
              <Tooltip
                title={
                  <div className="px-3 py-2">
                    {account.details && (
                      <>
                        <Label inverted>{texts.labels.details}</Label>
                        <Value className="mb-2">{account.details}</Value>
                      </>
                    )}
                    {account.ownerName && (
                      <>
                        <Label inverted>{texts.labels.accountHolderName}</Label>
                        <Value>{account.ownerName}</Value>
                      </>
                    )}
                  </div>
                }
              >
                <InfoCircleFilled
                  style={{
                    color: foundationColors["deep-purple-400"],
                    display: "block",
                  }}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      </Td>
      <Td>
        <Label>{texts.labels.balance}</Label>
        {isLoading ? (
          <Skeleton.Input active size="small" />
        ) : (
          <Value>
            {accountDetails?.data.latestBalanceAmount !== undefined &&
            accountDetails?.data.latestBalanceCurrency
              ? formatAmountCurrency(
                  accountDetails?.data.latestBalanceAmount / 100,
                  accountDetails?.data.latestBalanceCurrency
                )
              : "-"}
          </Value>
        )}
      </Td>
      <Td>
        <Label>
          {account.iban ? texts.labels.iban : texts.labels.accountNumber}
        </Label>
        <Value>
          {account.iban || account.accountNumber ? (
            <Copyable>{account.iban || account.accountNumber}</Copyable>
          ) : (
            "-"
          )}
        </Value>
      </Td>
      <Td>
        <Label>{texts.labels.lastSyncedAt}</Label>
        <Value>
          {moment(account.lastSyncedAt).format(DISPLAY_DATE_TIME_FORMAT)}
        </Value>
      </Td>
      <Td>
        <Label>{texts.labels.lastTransactionsAddedAt}</Label>
        <Value>
          {account.lastTransactionsAddedAt
            ? moment(account.lastTransactionsAddedAt).format(
                DISPLAY_DATE_TIME_FORMAT
              )
            : "-"}
        </Value>
      </Td>
      <Td>
        {account.archivedAt && (
          <>
            <Label>{texts.labels.archivedAt}</Label>
            <Value>
              {moment(account.archivedAt).format(DISPLAY_DATE_TIME_FORMAT)}
            </Value>
          </>
        )}
      </Td>
      <Td className="text-center">
        {account.archivedAt ? (
          <Tag>
            <Typography.Text type="secondary">
              {texts.labels.archived}
            </Typography.Text>
          </Tag>
        ) : (
          <Tag color="success">{texts.labels.active}</Tag>
        )}
      </Td>
      <Td />
    </Tr>
  );
};
